import { FC, useCallback, useEffect, useMemo, useState } from "react";
import { Skeleton } from "@mui/material";
import { GridColumnVisibilityModel, GridRowId, GridRowSelectionModel } from "@mui/x-data-grid";
import { useSetAtom } from "jotai/index";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { AppRoutes } from "../../../../../common/routesUtils";
import ServerTable from "../../../../../components/common/table/ServerTable";
import { useServerTableController } from "../../../../../components/common/table/ServerTable/ServerDataGrid/useServerTableController";
import { CustomColumnMenu } from "../../../../../components/common/table/TableData/CustomColumnMenu";
import { CustomDataGridFooter } from "../../../../../components/common/table/TableData/TableDataFooter";
import TableSelectActions from "../../../../../components/common/table/TableData/TableSelectActions";
import { useCustomTableDataStyles } from "../../../../../components/common/table/TableData/useCustomTableDataStyles";
import { PageType } from "../../../../../config/page.types";
import { useBreadcrumbs } from "../../../../../context/BreadcrumbsProvider";
import { useFiltersRegistry } from "../../../../../context/FiltersProvider";
import { usePageConfigurationState } from "../../../../../context/PageConfigrations/usePageConfigurationState";
import { EntitiesListDTO, EntityItemDTO } from "../../../../../datasources/api-client/data-contracts";
import { makeSnackbarMessage, publishSnackbarMessage } from "../../../../../state/view";
import { useCurrentTheme } from "../../../../../theme/theme";
import { GreenrouterRow } from "../../../ResultsOperationalUnits/OperationalUnitsGreenrouter/data";
import CustomNoRowsOverlay from "../components/CustomNoRowsOverlay";
import { getColumns, getRows } from "./data";

interface Props {
	data: EntitiesListDTO;
	isLoading: boolean;
	reload: () => void;
}

/**
 * The entity table for GreenRouter data.
 *
 * @constructor
 */
export const TableEntitiesGreenRouter: FC<Props> = ({ data, isLoading, reload }) => {
	const { theme } = useCurrentTheme();
	const tableSx = useCustomTableDataStyles();
	const publishMessage = useSetAtom(publishSnackbarMessage);
	const { appliedFilters } = useFiltersRegistry();
	const { t } = useTranslation(["common", "result.transport"]);
	const navigate = useNavigate();
	const { visitPage } = useBreadcrumbs();

	// Page configuration stored in localStorage
	const [config, setConfig] = usePageConfigurationState(PageType.ResultsTransportGreenRouterEntities, {
		emissionsKey: "co2Ttw",
		page: 0,
		pageSize: 10,
		sortField: undefined,
		sortDirection: undefined,
		columnVisibilityModel: {},

		entityPage: 0,
		entityEmissionsKey: "co2Ttw",
		entityColumnVisibilityModel: {},
	});

	// Generic table controller
	const {
		paginationModel,
		handlePaginationChange,
		sortModel,
		handleSortModelChange,
		columnVisibilityModel,
		handleColumnVisibilityChange,
	} = useServerTableController({
		config,
		setConfig,
	});

	// Build rows only when API data changes
	const rows = useMemo(() => (data ? getRows(data) : []), [data]);

	// Use the first item to resolve column measures
	const dataSample = data?.items?.[0];

	// delete
	// const { put: deleteNode, isLoading: deleteNodeLoading } = useWriteResource({
	// 	route: ApiRoutes.ConfigurationDeleteNodes,
	// });

	const [selectedRowIds, setSelectedRowIds] = useState<GridRowSelectionModel>({
		type: "include",
		ids: new Set<GridRowId>(),
	});

	// Create a stable dependency for applied filters
	const appliedFiltersKey = useMemo(() => JSON.stringify(appliedFilters), [appliedFilters]);

	// Reset selection and pagination when filters change
	useEffect(() => {
		setSelectedRowIds({
			type: "include",
			ids: new Set(),
		});

		if (config.page !== 0) {
			setConfig({ page: 0 });
		}
	}, [appliedFiltersKey]);

	const handleRowSelection = (newSelection: GridRowSelectionModel) => {
		setSelectedRowIds({
			type: newSelection.type,
			ids: new Set(newSelection.ids),
		});
	};

	// Resolve selected IDs for include and exclude modes
	const selectedRowsId: GridRowId[] = useMemo(() => {
		if (selectedRowIds.type === "include") {
			return Array.from(selectedRowIds.ids);
		}

		return rows
			.filter((row: GreenrouterRow) => !selectedRowIds.ids.has(row.id))
			.map((row: GreenrouterRow) => row.id);
	}, [selectedRowIds, rows]);

	const handleDeleteRows = async () => {
		try {
			// await deleteNode({ ids: selectedRowsId });
			console.log("DELETE", selectedRowsId);

			setSelectedRowIds({
				type: "include",
				ids: new Set(),
			});

			publishMessage(makeSnackbarMessage(t("common:delete rows dialog/success message"), "success"));
		} catch {
			publishMessage(
				makeSnackbarMessage(t("common:delete rows dialog/the deletion could not be completed"), "error")
			);
		}
	};

	const textPaletteColor = theme.palette.primary.contrastText;

	// Keep the navigation callback stable
	const navigateToEntity = useCallback(
		(row: EntityItemDTO) => {
			visitPage(
				PageType.ResultsTransportGreenRouterEntityDetail,
				false,
				row.description,

				// The identifier is passed as the ending subpath, because for now visitPage does NOT support multiple
				// path arguments. While this may change in the future, all routes are currently ending with `:id`, which
				// allows this use-case to be supported.
				`${row.id}/transports/greenrouter/${row.transportId}`
			);

			navigate(
				AppRoutes.ResultsEntityGreenRouterDetail.replace(":entityId", row.id).replace(
					":transportId",
					row.transportId
				)
			);
		},
		[visitPage, navigate]
	);

	// Resolve the emission column selected from the entity switch
	const activeEmissionsField = useMemo(
		() => (config.emissionsKey === "co2Ttw" ? "emissionTtw" : "emissionWtw"),
		[config.emissionsKey]
	);

	// Remove the inactive emission column from the grid definition
	const columns = useMemo(() => {
		const allColumns = getColumns(textPaletteColor, t, navigateToEntity, dataSample);

		return allColumns.filter((column) => {
			const isEmissionsColumn = column.field === "emissionTtw" || column.field === "emissionWtw";

			return !isEmissionsColumn || column.field === activeEmissionsField;
		});
	}, [textPaletteColor, t, navigateToEntity, dataSample, activeEmissionsField]);

	// Set defaults only for columns available in the current mode
	const defaultColumnVisibility = useMemo<GridColumnVisibilityModel>(() => {
		return columns.reduce<GridColumnVisibilityModel>((acc, column) => {
			acc[column.field] = true;
			return acc;
		}, {});
	}, [columns]);

	// Apply persisted visibility over the default model
	const mergedVisibilityModel = useMemo<GridColumnVisibilityModel>(
		() => ({
			...defaultColumnVisibility,
			...columnVisibilityModel,
		}),
		[defaultColumnVisibility, columnVisibilityModel]
	);

	if (isLoading) {
		return <Skeleton variant="rounded" height="100%" width="100%" />;
	}

	return (
		<ServerTable
			title={t("result.transport:list/greenrouter entities")}
			isLoading={isLoading}
			onReload={reload}
			actions={
				data?.itemsCount ? (
					<TableSelectActions
						selectedRowsId={selectedRowsId}
						onDeleteRows={handleDeleteRows} // TODO update query
						deleteLoading={false} // TODO update query
					/>
				) : null
			}
			getRowId={(row) => row.id}
			rows={rows}
			columns={columns}
			rowHeight={56}
			checkboxSelection
			rowSelectionModel={selectedRowIds}
			onRowSelectionModelChange={handleRowSelection}
			paginationModel={paginationModel}
			onPaginationModelChange={handlePaginationChange}
			rowCount={data?.itemsCount ?? 0}
			pageSizeOptions={[10, 25, 50]}
			sortModel={sortModel}
			onSortModelChange={handleSortModelChange}
			columnVisibilityModel={mergedVisibilityModel}
			onColumnVisibilityModelChange={handleColumnVisibilityChange}
			slots={{
				noRowsOverlay: () => <CustomNoRowsOverlay />,
				footer: CustomDataGridFooter,
				columnMenu: CustomColumnMenu,
			}}
			sx={tableSx}
		/>
	);
};
